import { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import { usAppointmentSlot } from '../../../../api/appointment-slot';
import { usAppointmentSlotDetails } from '../../../../api/appointment-slot-details';
import {
  useMultiStorePointOfSales,
  useMultiStoreSlotDetails,
  useMultiStoreSlots,
} from '../../../../api/multi-store';
import { DateInterface } from '../../../../interface/date';
import {
  MultiStoreCenter,
  MultiStoreCenterDay,
  MultiStorePointOfSale,
} from '../../../../interface/multi-store';
import { getClient } from '../../../../lib/info';
import { selectedType } from '../../../../store/appointment-type';
import { selectedCoords } from '../../../../store/coordinates';
import { useAppSelector } from '../../../../store/hooks';
import {
  selectedIncludedPointOfSaleIds,
  selectedMultiStoreOrigin,
} from '../../../../store/multi-store';
import {
  selectedFilterCustomPointOfSaleList,
  selectedPointOfSale,
} from '../../../../store/point-of-sale';
import { isUserURLParams, selectedUser } from '../../../../store/user';
import {
  getLoadingCenterIds,
  getUnavailableCenterIds,
  isAllSettled,
  resolveIncludedCenters,
} from '../../../../utils/multi-store/centers';
import {
  buildDateInterface,
  getActiveDates,
  mergeDates,
} from '../../../../utils/multi-store/dates';
import { getElsewhereOnly } from '../../../../utils/multi-store/elsewhere';
import { normalizeFilterPointOfSaleIds } from '../../../../utils/multi-store/filter';
import { mergeHours } from '../../../../utils/multi-store/hours';

interface MultiStoreRequest {
  serviceId: string;
  userId?: string;
  filter?: string[];
}

// Filtro pointOfSaleIds del link: le alternative restano fra i centri della lista
const useMultiStoreFilter = (): string[] | undefined => {
  const filterList = useAppSelector(selectedFilterCustomPointOfSaleList);
  return useMemo(
    () => normalizeFilterPointOfSaleIds(filterList),
    [filterList]
  );
};

// Parametri comuni degli slot multi-store. L'operatore si passa solo se selezionabile: il
// backend decide centro per centro se tenerlo o usare "qualsiasi operatore" (isAnyUser)
const useMultiStoreRequest = (): MultiStoreRequest => {
  const {
    company: { isUserSelectable },
  } = getClient();
  const { id: serviceId } = useAppSelector(selectedType);
  const user = useAppSelector(selectedUser);
  const filter = useMultiStoreFilter();

  return {
    serviceId,
    userId: isUserSelectable && user !== undefined ? user.id : undefined,
    filter,
  };
};

// Centri alternativi rispetto all'origin, con la distanza dal cliente se ha indicato la sua
// posizione nello step "dove", altrimenti dall'origin
export const useMultiStoreCandidates = (): UseQueryResult<
  MultiStorePointOfSale[]
> => {
  const origin = useAppSelector(selectedMultiStoreOrigin);
  const coords = useAppSelector(selectedCoords);
  const { id: serviceId } = useAppSelector(selectedType);
  const filter = useMultiStoreFilter();

  return useMultiStorePointOfSales(
    serviceId,
    origin.id,
    coords?.lat,
    coords?.lng,
    filter
  );
};

export const useIncludedCenters = (): MultiStoreCenter[] => {
  const includedIds = useAppSelector(selectedIncludedPointOfSaleIds);
  const origin = useAppSelector(selectedMultiStoreOrigin);
  const current = useAppSelector(selectedPointOfSale);
  const { data: candidates } = useMultiStoreCandidates();

  return useMemo(
    () =>
      resolveIncludedCenters(includedIds, origin, current, candidates ?? []),
    [includedIds, origin, current, candidates]
  );
};

export interface MultiStoreWeek {
  // Solo il centro corrente: gli altri entrano nell'unione man mano che rispondono
  isPending: boolean;
  // Nessuna richiesta in attesa, alternativi compresi: solo allora si avanza di giorno
  allSettled: boolean;
  data?: DateInterface;
  // giorni liberi solo negli altri centri: nessuno finché il centro corrente non risponde
  elsewhereDates: string[];
  unavailableIds: string[];
  loadingIds: string[];
}

// Giorni della finestra liberi in almeno un centro incluso. Il centro corrente usa l'hook
// classico (stessa cache e stesse regole del WeekPicker) e non dipende mai dalle API nuove,
// nemmeno dalla loro lentezza; gli altri usano le API multi-store, entrano nell'unione
// quando rispondono e, se non disponibili, ne restano fuori.
export const useMultiStoreWeek = (
  startDate: string,
  endDate: string,
  centers: MultiStoreCenter[]
): MultiStoreWeek => {
  const {
    company: { isUserSelectable },
  } = getClient();
  const { id: currentId } = useAppSelector(selectedPointOfSale);
  const user = useAppSelector(selectedUser);
  const isURLParams = useAppSelector(isUserURLParams);
  const { serviceId, userId, filter } = useMultiStoreRequest();
  const others = centers.filter((center) => !center.isCurrent);

  const current = usAppointmentSlot(
    isUserSelectable,
    serviceId,
    currentId,
    startDate,
    endDate,
    user?.id,
    isURLParams
  );
  const results = useMultiStoreSlots(
    others.map((center) => center.pointOfSale.id),
    serviceId,
    startDate,
    endDate,
    userId,
    filter
  );
  const currentDates = getActiveDates(current.data);
  const otherDates = results.map((result) =>
    result.isUnavailable ? [] : result.data?.dates ?? []
  );

  return {
    isPending: current.isPending,
    allSettled: isAllSettled([current, ...results]),
    data: current.isPending
      ? undefined
      : buildDateInterface(
          startDate,
          endDate,
          mergeDates([currentDates, ...otherDates])
        ),
    elsewhereDates: getElsewhereOnly(
      currentDates,
      otherDates,
      !current.isPending
    ),
    unavailableIds: getUnavailableCenterIds(others, results),
    loadingIds: getLoadingCenterIds(others, results),
  };
};

export interface MultiStoreDay {
  // Solo il centro corrente: gli orari degli altri si aggiungono quando arrivano
  isPending: boolean;
  // Dati del centro corrente caricati e non in aggiornamento: solo allora si controlla l'ora
  isCurrentSettled: boolean;
  currentTimes: string[];
  hours: string[];
  centers: MultiStoreCenterDay[];
  unavailableIds: string[];
  loadingIds: string[];
}

// Orari del giorno dei centri inclusi: il corrente con l'hook classico dell'HourPicker,
// gli altri con le API multi-store (aggiunti quando rispondono, esclusi se non disponibili)
export const useMultiStoreDay = (
  date: string | undefined,
  centers: MultiStoreCenter[]
): MultiStoreDay => {
  const {
    company: { isUserSelectable },
  } = getClient();
  const { id: currentId } = useAppSelector(selectedPointOfSale);
  const user = useAppSelector(selectedUser);
  const isURLParams = useAppSelector(isUserURLParams);
  const { serviceId, userId, filter } = useMultiStoreRequest();
  const others = centers.filter((center) => !center.isCurrent);

  const current = usAppointmentSlotDetails(
    isUserSelectable,
    serviceId,
    currentId,
    date,
    user?.id,
    isURLParams
  );
  const results = useMultiStoreSlotDetails(
    others.map((center) => center.pointOfSale.id),
    serviceId,
    date,
    userId,
    filter
  );

  const currentTimes = current.data ?? [];
  const dayCenters = centers.flatMap((center): MultiStoreCenterDay[] => {
    if (center.isCurrent) {
      return [{ ...center, isAnyUser: false, times: currentTimes }];
    }
    const result = results[others.indexOf(center)];
    if (
      result === undefined ||
      result.isUnavailable ||
      result.data === undefined
    ) {
      return [];
    }
    return [
      { ...center, isAnyUser: result.data.isAnyUser, times: result.data.times },
    ];
  });

  return {
    isPending: current.isPending,
    isCurrentSettled: !current.isPending && !current.isFetching,
    currentTimes,
    hours: mergeHours(dayCenters.map((center) => center.times)),
    centers: dayCenters,
    unavailableIds: getUnavailableCenterIds(others, results),
    loadingIds: getLoadingCenterIds(others, results),
  };
};
